import { Button, ButtonGroup } from '@upmc/ims.component-library';
import Base from '../Base/Base';
import {
	GetApplicationsInfoDetails,
	GetApplicationsInfoIndex,
	UserCanEditAppInfoPage,
	UpdateApplicationInformation,
	GetLocationOptions,
} from './ApplicationInformation_api';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from 'context/User/UserContext';
import styles from './ApplicationInformation.module.scss';
import MaterialIcon from '@material/react-material-icon';
import AppInfoView from './components/AppInfoView';
import AppInfoEdit from './components/AppInfoEdit';
import ApplicationsSearch from '../../components/AppUtils/ApplicationsSearch';
import { useLocation } from 'react-router-dom';

const ApplicationInformation = () => {
	const MODES = { VIEW: 'view', EDIT: 'edit' };
	const [apps, setApps] = useState(null);
	const [activeApps, setActiveApps] = useState(null);
	const [selectedApp, setSelectedApp] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [userCanEdit, setUserCanEdit] = useState(false);
	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user =
		contextUser.userInfo.impersonationProfile ??
		contextUser.userInfo.userProfile;

	const [mode, setMode] = useState(MODES.VIEW);
	const [copyOfApp, setCopyOfApp] = useState({ ...selectedApp });
	const [locationOptions, setLocationOptions] = useState([]);

	const location = useLocation();
	const initialAppID = new URLSearchParams(location.search).get('UPMCAppId');

	useEffect(() => {
		setIsLoading(true);
		GetApplicationsInfoIndex()
			.then(response => {
				let apps = response;

				setApps(apps);
			})
			.then(result => {
				setIsLoading(false);
			});

		GetLocationOptions().then(response => {
			let locations = response;
			setLocationOptions(locations);
		});
	}, []);

	useEffect(() => {
		//when mode == view, ensure the displayed information is always in line with data from server
		if (mode === MODES.VIEW && selectedApp) {
			GetApplicationsInfoDetails(selectedApp.id)
				.then(response => {
					let appModel = response?.applications[0];
					setSelectedApp(appModel);
				})
				.then(res => setIsLoading(false));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mode]);

	useEffect(() => {
		//if initialAppID is not null or undefined, set selected app accordingly, if not set selected app to the first app in the results
		if (apps && apps.applications[0]) {
			setIsLoading(true);

			if (initialAppID) {
				let preSelectedApp = apps.applications.find(function (app) {
					return app.appInfo.upmc_App_ID === parseInt(initialAppID);
				});

				if (preSelectedApp) {
					GetApplicationsInfoDetails(preSelectedApp.id)
						.then(response => {
							let appModel = response.applications[0];
							setSelectedApp(appModel);
						})
						.then(res => setIsLoading(false));
				}
			} else {
				GetApplicationsInfoDetails(apps.applications[0].id)
					.then(response => {
						let appModel = response.applications[0];
						setSelectedApp(appModel);
					})
					.then(res => setIsLoading(false));
			}

			var activeApps = apps?.applications.filter(
				application => application?.isActive === true
			);
			setActiveApps(activeApps);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [apps]);

	useEffect(() => {
		if (selectedApp && user.ID) {
			UserCanEditAppInfoPage(user.ID, selectedApp.appInfo.upmc_App_ID)
				.then(response => {
					setUserCanEdit(response);
					setMode(MODES.VIEW);
				})
				.then(x => setCopyOfApp({ ...selectedApp }));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedApp, user]);

	const handleAppChange = e => {
		const appID = e.target.value;
		GetApplicationsInfoDetails(appID).then(response =>
			setSelectedApp(response.applications[0])
		);
	};

	const ToggleModeButton = () => {
		if (mode === MODES.VIEW) {
			return (
				<Button
					onClick={e => setMode(MODES.EDIT)}
					variant='outlined'
					startIcon={<MaterialIcon icon='edit' />}
					id='infoEditbtn'
				>
					Edit
				</Button>
			);
		} else if (mode === MODES.EDIT) {
			return (
				<ButtonGroup center>
					<Button
						onClick={e => {
							setMode(MODES.VIEW);
						}}
						variant='outlined'
						startIcon={<MaterialIcon icon='cancel' />}
						id='editCancelbtn'
					>
						Cancel
					</Button>
					<Button
						onClick={e => handleSave(e)}
						outlined
						startIcon={<MaterialIcon icon='save' />}
						id='cancelSavebtn'
					>
						Save
					</Button>
				</ButtonGroup>
			);
		}
	};

	const handleSave = e => {
		setIsLoading(true);
		copyOfApp.EsoId = user.ID;
		copyOfApp.Locations = copyOfApp.appInfo.locations.locationData;
		copyOfApp.SecurityContacts = copyOfApp.appInfo.securityContact;
		copyOfApp.PrivacyContacts = copyOfApp.appInfo.privacyContact;
		copyOfApp.TechnicalTeam = copyOfApp.appInfo.technicalTeam;
		copyOfApp.Owners = copyOfApp.appInfo.owners;

		UpdateApplicationInformation(copyOfApp)
			.then(response => {
				setMode(MODES.VIEW);
				GetApplicationsInfoDetails(copyOfApp.id).then(resp =>
					setSelectedApp(resp.applications[0])
				);
			})
			.then(x => setIsLoading(false));
	};

	return (
		<Base
			loading={isLoading}
			title={'Application Information'}
			helpLink={''}
			className={styles.APPLY_FONT}
		>
			{apps && activeApps ? (
				<>
					<div className={styles.selectionControls}>
						<div className={styles.flex_Vert}>
							<ApplicationsSearch
								options={activeApps}
								onChange={handleAppChange}
								placeholder={'...Search By App Name or Alias'}
							/>
						</div>
					</div>
					<div className={styles.SelectedAppHeader}>
						<h3>{selectedApp?.appInfo?.applicationName}</h3>
						{/* //display edit button only if currentUser is authorized to edit */}
						{userCanEdit === true ? <ToggleModeButton /> : null}
					</div>
					{mode === MODES.VIEW ? (
						<AppInfoView selectedApp={selectedApp} />
					) : (
						<AppInfoEdit
							selectedApp={selectedApp}
							userCanEdit={userCanEdit}
							copyOfApp={copyOfApp}
							setCopyOfApp={setCopyOfApp}
							locationOptions={locationOptions}
						/>
					)}
				</>
			) : null}
		</Base>
	);
};

export default ApplicationInformation;
